import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AppLayout from "@/components/AppLayout";
import Chat from "@/pages/Chat";
import Vault from "@/pages/Vault";
import People from "@/pages/People";
import Meetings from "@/pages/Meetings";
import MeetingListener from "@/pages/MeetingListener";
import BuildMode from "@/pages/BuildMode";
import Studio from "@/pages/Studio";
import Briefings from "@/pages/Briefings";
import Inbox from "@/pages/Inbox";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="ph-shell flex items-center justify-center" style={{ minHeight: "100vh" }}>
        <div className="text-dim" style={{ fontSize: 13 }}>Listening…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Router() {
  const location = useLocation();
  // Detect OAuth fragment synchronously during render (before ProtectedRoute)
  if (location.hash && (location.hash.includes("session_id=") || location.hash.includes("ms_token="))) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <Protected>
            <AppLayout />
          </Protected>
        }
      >
        <Route index element={<Navigate to="/chat" replace />} />
        <Route path="chat" element={<Chat />} />
        <Route path="chat/:cid" element={<Chat />} />
        <Route path="vault" element={<Vault />} />
        <Route path="vault/:docId" element={<Vault />} />
        <Route path="people" element={<People />} />
        <Route path="meetings" element={<Meetings />} />
        <Route path="meetings/listen/:mid" element={<MeetingListener />} />
        <Route path="meetings/:mid" element={<Meetings />} />
        <Route path="studio" element={<Studio />} />
        <Route path="briefings" element={<Briefings />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="build" element={<BuildMode />} />
        <Route path="admin" element={<Admin />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/chat" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="ph-shell">
      <BrowserRouter>
        <AuthProvider>
          <Router />
          <ThemedToaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  return (
    <Toaster
      position="bottom-right"
      theme={isDark ? "dark" : "light"}
      // 70 sits above the voice overlay (z:60) so "Saved" / error feedback is
      // always visible during a hands-free voice session — otherwise you tap
      // Save (or say "save it"), the work happens, but the confirmation toast
      // is buried under the overlay backdrop and the action looks broken.
      style={{ zIndex: 70 }}
      toastOptions={{
        style: {
          background: isDark ? "#232323" : "#FFFFFF",
          color: isDark ? "#F9FAFB" : "#0F172A",
          border: `1px solid ${isDark ? "#3A3A3D" : "#E5E7EB"}`,
        },
      }}
    />
  );
}
